import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, BadgeCheck } from "lucide-react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import Banner from "../../components/ui/Banner/Banner.jsx";
import PageHeader from "../../components/marketing/PageHeader.jsx";
import { money } from "../../utils/format.js";
import { getServiceFeeRates } from "../../services/lookupService.js";
import { getSubscriptionPackages } from "../../services/subscriptionService.js";
import SeoHead from "../../seo/SeoHead.jsx";
import { ROUTE_META } from "../../seo/siteMeta.js";
import { breadcrumbSchema } from "../../seo/schema.js";

// "1 vehicle" / "2 vehicles" / "3 - 24 vehicles" / "501+ vehicles" — mirrors
// SubscriptionUsageServiceImpl.describeVehicleBand's band-labelling logic on
// the backend, applied here to a service_fee_rate row's min/maxVehicles
// instead of a subscription category's.
function bandLabel(minVehicles, maxVehicles) {
  if (minVehicles == null && maxVehicles == null) return "Any vehicle count";
  if (minVehicles === maxVehicles) return `${minVehicles} vehicle${minVehicles === 1 ? "" : "s"}`;
  if (maxVehicles == null) return `${minVehicles}+ vehicles`;
  return `${minVehicles} - ${maxVehicles} vehicles`;
}

// The per-return price once a category's package discount is applied.
//
// Every tier in a category currently lands on the same per-return figure to
// the cent - the discount is a flat percentage per category, not quantity-
// tiered, so buying 200 returns costs exactly the same each as buying 5. This
// still takes the MINIMUM rather than assuming they are equal: if a real
// quantity-tiered discount is ever seeded, the column keeps quoting the true
// best price instead of whichever tier happened to be first in the array.
//
// Returns null when a category has no usable package, so the caller can render
// a dash rather than a fabricated number.
function bestPerReturn(category) {
  const usable = (category?.packages || []).filter((p) => Number(p.returnsIncluded) > 0);
  if (!usable.length) return null;
  return Math.min(...usable.map((p) => Number(p.finalAmount) / Number(p.returnsIncluded)));
}

// What other 2290 e-file services charge for a single-vehicle return.
//
// PROVENANCE: supplied and verified by the business owner on 2026-08-19 - not
// scraped, computed, or estimated by this app, and not derived from anything
// in this repo. It is a factual claim about third parties published on a
// public page, so it carries an obligation the rest of this file does not:
// competitor pricing moves, and a stale range here becomes a false comparison.
// RE-VERIFY before each prod release and whenever a competitor is known to
// have repriced. If it cannot be re-verified, delete the comparison rather
// than let it age - the page stands on its own price without it.
//
// 2026-08-20: lowered 7.99 -> 7.49. Re-checked against the competitors'
// published pricing pages; the lowest advertised single-vehicle fee found is
// 7.49 (2290.us, https://www.2290.us/ - "1 vehicle $7.49", read 2026-08-20).
// 7.99 is still advertised by a service in the range but is no longer the
// floor, and quoting it as the floor overstated our margin by 0.50. NOTE: this
// figure was read off a public pricing page by an agent, NOT supplied by the
// owner - it does not yet meet the provenance bar above and needs owner
// sign-off. The source is named here, in the comment, purely so the next
// RE-VERIFY knows where to look; the rendered page still names nobody.
//
// Deliberately a range with no competitor named: naming a company alongside a
// price is a specific claim about that company on a given date, a materially
// higher bar to keep true than a market range.
const COMPETITOR_LOW = 7.49;
// Not a ceiling - the owner confirmed (2026-08-19) that some services charge
// MORE than this. It is the top of the range actually checked, so the page
// says "$20+" and the bar runs off the right edge under a fade rather than
// stopping at a tidy end cap. Drawing a hard end at 20 would assert a maximum
// nobody verified; an open end asserts only what was seen, which is what the
// footnote says too.
const COMPETITOR_HIGH_SEEN = 20.0;
const COMPETITOR_CHECKED = "August 2026";

// Bars are drawn to one shared scale, so the comparison is honest to the eye
// as well as in the numbers - bars not drawn to scale would be a visual claim
// the figures do not support. The scale tops out at the highest price actually
// checked; anything past it is the open end, not a plotted value.
const SCALE_MAX = COMPETITOR_HIGH_SEEN;
const pctNum = (n) => Math.min((n / SCALE_MAX) * 100, 100);
const pct = (n) => `${pctNum(n)}%`;

// Lowest-price panel. The headline figure is NOT hardcoded - it is the live
// 1-vehicle "original" band from service_fee_rate, the same source the fee
// table further down reads, so this panel can never advertise a price the app
// does not actually charge. If that band is missing (API down, or a rate sheet
// without a 1-vehicle band), the whole panel renders nothing rather than
// falling back to a stale or guessed number: not advertising a price is fine,
// advertising the wrong one is not.
//
// The comparison is service fee vs competitor advertised price. This page
// deliberately says nothing about the $1.00 card-processing surcharge (owner's
// call, 2026-08-19) - it is still disclosed to the filer as its own "Processing
// fee" line in the payment-step breakdown before anything is charged, so this
// is a quieter marketing page, not an undisclosed fee. Worth knowing when
// reading the comparison: a competitor's advertised start may or may not
// include their own processing, so this is like-for-like on advertised price,
// not necessarily on final total. It renders only while our fee genuinely sits
// below where competitors start - if a future reprice pushed it above
// COMPETITOR_LOW, the claim drops out instead of going stale.
//
// Every line in "and nothing is stripped out to get there" is a real,
// verified capability - the same no-false-claims bar Home.jsx's FEATURES list
// is held to (see its "Mobile Friendly removed 2026-08-07" note):
//   - VIN corrections free      -> driven off the live rate, not asserted:
//                                  only rendered when the API actually
//                                  reports vincorrection at 0.
//   - refile after a reject     -> ServiceFeeServiceImpl.createIntent looks
//                                  the fee up by tax return id and refuses a
//                                  second charge once one is SUCCESS, so
//                                  fixing and retransmitting a rejected
//                                  return genuinely costs nothing more.
//   - bulk upload to 500        -> utils/bulkImport.js MAX_ROWS = 500.
//   - Schedule 1 / PDFs / history -> the shipped features behind
//                                  /dashboard/filings.
function LowestPricePanel({ soloBand, vinCorrectionFree }) {
  if (!soloBand) return null;

  const fee = Number(soloBand.feeAmount);
  const beatsMarket = fee < COMPETITOR_LOW;

  const included = [
    vinCorrectionFree && "VIN corrections are free — no service fee, ever",
    "Rejected by the IRS? Fix it and refile at no extra charge",
    "Bulk-upload up to 500 vehicles from one Excel template",
    "Stamped Schedule 1, both PDFs, and your full filing history",
  ].filter(Boolean);

  return (
    <Panel padded className="!bg-[var(--accent-soft)] mt-9 !p-[30px]">
      <div className="flex flex-wrap items-start justify-between gap-y-8 gap-x-10">
        <div className="min-w-[280px] flex-1 max-w-[460px]">
          <div className="font-disp uppercase tracking-[.1em] text-[11px] text-[var(--accent)] font-semibold flex items-center gap-1.5">
            <BadgeCheck size={14} /> The lowest price for a single-vehicle 2290
          </div>
          <div className="flex items-baseline gap-2.5 mt-2.5">
            <span className="text-[40px] leading-none font-extrabold font-disp tracking-[-0.03em]">{money(fee)}</span>
            <span className="text-[var(--muted)] text-[15px]">one vehicle, e-filed to the IRS</span>
          </div>
          {beatsMarket && (
            <div className="mt-5">
              <div className="grid gap-3.5">
                <div>
                  <div className="text-[12px] font-semibold mb-1.5">EasyFile2290</div>
                  {/* h-[22px] row with a 9px track centred in it, so a price
                      label can sit on the same line as the bar without the
                      row collapsing around the text. */}
                  <div className="relative h-[22px]">
                    <div className="absolute left-0 right-0 top-[7px] h-[9px] rounded-full bg-[var(--line)]" />
                    <div
                      className="absolute left-0 top-[7px] h-[9px] rounded-full bg-[var(--accent)]"
                      style={{ width: pct(fee) }}
                    />
                    <span
                      className="absolute top-0 leading-[22px] text-[13px] font-mono font-bold whitespace-nowrap"
                      style={{ left: `calc(${pct(fee)} + 10px)` }}
                    >
                      {money(fee)}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-[12px] text-[var(--muted)] mb-1.5">Other 2290 e-file services</div>
                  <div className="relative h-[22px]">
                    <div className="absolute left-0 right-0 top-[7px] h-[9px] rounded-full bg-[var(--line)]" />
                    <div
                      className="absolute top-[7px] right-0 h-[9px] bg-[var(--muted)] opacity-40"
                      style={{
                        left: pct(COMPETITOR_LOW),
                        // Fades out instead of ending: the right edge is an
                        // open range, not a measured maximum.
                        maskImage: "linear-gradient(to right, #000 55%, transparent 100%)",
                        WebkitMaskImage: "linear-gradient(to right, #000 55%, transparent 100%)",
                      }}
                    />
                    {/* Right-aligned to just before their bar starts: the
                        label sits at the point their pricing begins, so the
                        empty span between the two labels IS the saving. */}
                    <span
                      className="absolute top-0 leading-[22px] text-[12.5px] font-mono text-[var(--muted)] whitespace-nowrap"
                      style={{ right: `calc(${100 - pctNum(COMPETITOR_LOW)}% + 10px)` }}
                    >
                      {money(COMPETITOR_LOW)} – {money(COMPETITOR_HIGH_SEEN)}+
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-[12px] text-[var(--muted)] mt-2.5 leading-[1.55]">
                Competitor pricing for a single-vehicle return, checked {COMPETITOR_CHECKED}. Some charge more than{" "}
                {money(COMPETITOR_HIGH_SEEN)}. Ours sits below where their pricing starts.
              </p>
            </div>
          )}
        </div>

        <div className="min-w-[280px] flex-1 max-w-[420px]">
          <div className="font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)] font-semibold mb-2.5">
            And nothing is stripped out to get there
          </div>
          <ul className="grid gap-2">
            {included.map((line) => (
              <li key={line} className="flex items-start gap-2.5 text-[14px] leading-[1.55]">
                <Check size={16} className="text-[var(--ok)] flex-none mt-[3px]" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Panel>
  );
}

const meta = ROUTE_META["/pricing"];

const Pricing = () => {
  const [feeRates, setFeeRates] = useState([]);
  const [feeRatesLoading, setFeeRatesLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getServiceFeeRates()
      .then((rates) => {
        if (!cancelled) setFeeRates(Array.isArray(rates) ? rates : []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setFeeRatesLoading(false);
      });
    getSubscriptionPackages()
      .then((cats) => {
        if (!cancelled) setCategories(Array.isArray(cats) ? cats : []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The vehicle-count-banded schedule (original + final filings share it) is
  // the primary table here — what most filers actually pay per return.
  const originalBands = feeRates.filter((r) => r.filingType === "original");
  const amendedFlat = feeRates.find((r) => r.filingType === "amended");
  const vinCorrectionBands = feeRates.filter((r) => r.filingType === "vincorrection");
  const vinCorrectionMatchesOriginal =
    vinCorrectionBands.length === originalBands.length &&
    vinCorrectionBands.every((r, i) =>
      r.minVehicles === originalBands[i]?.minVehicles &&
      r.maxVehicles === originalBands[i]?.maxVehicles &&
      Number(r.feeAmount) === Number(originalBands[i]?.feeAmount)
    );
  // A single row with no vehicle-count range at all (min AND max both null) is
  // a flat fee, not a band - same shape `amended` uses. VIN correction is
  // currently seeded this way at $0 (free, 2026-08-13 decision), but this
  // stays generic (any flat amount, not just $0) so a future non-zero flat
  // fee still renders correctly without another copy change here.
  const vinCorrectionFlat =
    vinCorrectionBands.length === 1 && vinCorrectionBands[0].minVehicles == null && vinCorrectionBands[0].maxVehicles == null
      ? vinCorrectionBands[0]
      : null;
  const vinCorrectionFree = !!vinCorrectionFlat && Number(vinCorrectionFlat.feeAmount) === 0;

  // Join each service_fee_rate band to its subscription category by unit
  // price: a category's unitPrice IS that band's feeAmount (SOLO 5.99 <-> the
  // 1-vehicle band, GROWING 42.99 <-> 25-100, and so on). Matching on the
  // number survives categories being renamed or reordered, where a hardcoded
  // code->band map would silently drift. Keyed to 2dp so 5.99 and 5.990 agree.
  // A band with no matching category simply has no bulk figure - the cell
  // renders a dash instead of inventing one.
  const bulkByFee = new Map();
  categories.forEach((cat) => {
    const unit = Number(cat.packages?.[0]?.unitPrice);
    const per = bestPerReturn(cat);
    if (Number.isFinite(unit) && per != null) bulkByFee.set(unit.toFixed(2), per);
  });
  // Only widen the table once real package data is in hand. While it is
  // loading, or if the call failed, the table stays two columns rather than
  // showing a column of dashes.
  const showBulk = !categoriesLoading && bulkByFee.size > 0;
  const bulkFor = (feeAmount) => bulkByFee.get(Number(feeAmount).toFixed(2));
  // Saving is computed from the two prices on the SAME row rather than read off
  // discountValue, so the percentage can never contradict the numbers printed
  // beside it. They agree today (12/14/16/18/20/22) - this keeps them agreeing
  // if a rate is ever changed without its category's discountValue following.
  const savingPctFor = (feeAmount) => {
    const fee = Number(feeAmount);
    const bulk = bulkFor(feeAmount);
    if (!Number.isFinite(fee) || fee <= 0 || bulk == null) return null;
    return Math.round(((fee - bulk) / fee) * 100);
  };
  // The smallest bundle anyone can buy, read from the data rather than written
  // into the copy. Every category currently starts at 5, so the column reads
  // "Bulk price (5+)" - if a smaller tier is ever seeded, the header follows it
  // instead of quietly becoming a false statement about the minimum.
  const bulkQuantities = categories
    .flatMap((c) => (c.packages || []).map((pk) => Number(pk.packageQuantity)))
    .filter((n) => Number.isFinite(n) && n > 0);
  const minBulkQty = bulkQuantities.length ? Math.min(...bulkQuantities) : null;
  // Highest saving on offer, for the section copy. Derived, not asserted.
  const bestSavingPct = showBulk
    ? Math.max(...originalBands.map((r) => savingPctFor(r.feeAmount)).filter((n) => n != null))
    : null;

  return (
    <div className="max-w-[1180px] mx-auto px-[22px] py-[46px] pb-[70px]">
      <SeoHead
        title={meta.title}
        description={meta.description}
        path="/pricing"
        keywords={meta.keywords}
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Pricing", path: "/pricing" }])]}
      />
      <PageHeader eyebrow="Pricing" title="Pay per return, or save with a package">
        File one return and pay the e-filing service fee for it, or buy a discounted bundle of return credits
        upfront and skip the per-vehicle fee on every return it covers. Every price below is exactly what the app
        charges — nothing is marked up for this page.
      </PageHeader>

      {/* ============ LOWEST-PRICE COMPARISON ============ */}
      {!feeRatesLoading && (
        <LowestPricePanel
          soloBand={originalBands.find((r) => r.minVehicles === 1 && r.maxVehicles === 1)}
          vinCorrectionFree={vinCorrectionFree}
        />
      )}

      {/* ============ PAY PER RETURN ============ */}
      <div className="mt-10">
        <h2 className="text-[22px] font-disp font-extrabold tracking-tight mb-1">Pay per return</h2>
        <p className="text-[var(--muted)] text-[15px] mb-5 max-w-[62ch]">
          The e-filing service fee is billed once per return, based on how many vehicles are on it — the more
          trucks on one return, the more it costs to file, same as the IRS's own per-vehicle tax.
        </p>

        {feeRatesLoading ? (
          <div className="p-8 text-center text-[var(--muted)]">Loading current rates…</div>
        ) : originalBands.length === 0 ? (
          <div className="p-8 text-center text-[var(--muted)]">Pricing is temporarily unavailable.</div>
        ) : (
          <Panel className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[560px]">
                <thead>
                  <tr>
                    {(showBulk
                      ? [
                          "Vehicles on the return",
                          "Pay per return",
                          minBulkQty ? `Bulk price (${minBulkQty}+)` : "Bulk price",
                          "You save",
                        ]
                      : ["Vehicles on the return", "Service fee"]
                    ).map((h) => (
                      <th
                        key={h}
                        className="text-left p-3 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)] font-semibold"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {originalBands.map((r) => (
                    <tr key={`${r.minVehicles}-${r.maxVehicles}`}>
                      <td className="p-3 border-b border-[var(--line)]">{bandLabel(r.minVehicles, r.maxVehicles)}</td>
                      <td className="p-3 border-b border-[var(--line)] font-mono font-semibold">{money(r.feeAmount)}</td>
                      {showBulk && (
                        <>
                          <td className="p-3 border-b border-[var(--line)] font-mono font-semibold text-[var(--accent)]">
                            {bulkFor(r.feeAmount) != null ? money(bulkFor(r.feeAmount)) : "—"}
                          </td>
                          <td className="p-3 border-b border-[var(--line)] font-mono font-semibold text-[var(--ok)]">
                            {savingPctFor(r.feeAmount) != null ? `${savingPctFor(r.feeAmount)}% off` : "—"}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        )}

        {!feeRatesLoading && (amendedFlat || vinCorrectionBands.length > 0) && (
          <Banner tone="info" className="mt-4 max-w-[820px]">
            {amendedFlat && (
              <>
                An amended return (weight increase or mileage exceeded) is a flat {money(amendedFlat.feeAmount)},
                regardless of vehicle count.{" "}
              </>
            )}
            {vinCorrectionBands.length > 0 &&
              (vinCorrectionFree
                ? "A VIN correction is free — no service fee."
                : vinCorrectionMatchesOriginal
                ? "A VIN correction follows this same per-vehicle-count schedule."
                : vinCorrectionFlat
                ? `A VIN correction is a flat ${money(vinCorrectionFlat.feeAmount)}, regardless of vehicle count.`
                : "A VIN correction has its own per-vehicle-count schedule.")}
          </Banner>
        )}
      </div>

      {/* ============ PREPAID RETURNS ============ */}
      {/* This replaced a 7-card grid where each card carried its own 5-option
          quantity dropdown: 35 combinations a visitor had to work through to
          discover 7 numbers. The quantity never changed the per-return price
          (the discount is a flat percentage per category, so 200 returns cost
          the same each as 5), so the cards were asking for a decision that
          changed only the invoice total. Those 7 numbers are now the bulk-price
          column of the table above, readable without a single interaction.
          Choosing how many to buy belongs in the purchase flow, where the
          customer has already decided to buy - Dashboard's
          SubscriptionPurchaseModal still does exactly that. */}
      <div className="mt-16">
        <h2 className="text-[22px] font-disp font-extrabold tracking-tight mb-1">
          Filing more than one return? Pay less on every one.
        </h2>
        <p className="text-[var(--muted)] text-[15px] mb-3 max-w-[62ch]">
          Buy {minBulkQty ?? 5} or more returns at once and each one costs the bulk price above
          {bestSavingPct != null ? ` — up to ${bestSavingPct}% off` : ""}. Returns never expire, so anything you
          don’t use this year is still there next year, and it is a one-time purchase rather than a recurring
          charge.
        </p>
        {categoriesLoading ? (
          <div className="text-[var(--muted)] text-[15px]">Loading current packages…</div>
        ) : categories.length === 0 ? (
          <div className="text-[var(--muted)] text-[15px]">No packages are available right now.</div>
        ) : null}
      </div>

      <Panel padded className="!bg-[var(--accent-soft)] flex flex-wrap items-center justify-between gap-6 mt-[54px] !p-[30px]">
        <div>
          <h2 className="text-[22px] font-disp">Ready to file?</h2>
          <p className="text-[var(--muted)] mt-1">Create an account and see your exact price before you pay anything.</p>
        </div>
        <Button as={Link} to="/login?tab=up" size="lg" className="no-underline flex-none">
          Get started free <ArrowRight size={17} />
        </Button>
      </Panel>
    </div>
  );
};

export default Pricing;
